import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  LinearProgress,
  Toolbar,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import MobileLayout from "../MobileLayout";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { setQuizChance, setQuizScore } from "../game/slice";
import { recordQuestion } from "../lib/learningStats";
import { RootState } from "../redux/store";
import { useTranslation } from "react-i18next";
import breeferLogo from "../asset/logo/LOGO_ORANGE.svg";
import BreefReportService from "../game/BreefReportService";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import analytics from "../analytics/track";
import { useBreefDetails } from "../game/gameService";

interface Question {
  question: string;
  options: string[];
  answer: number;
}
/** How long the right answer is shown when nobody got it in time */
const REVEAL_MS = 2500;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const useStyles: any = makeStyles((theme: { palette: { grey: any[] } }) => ({
  linearProgress: {
    border: "1px solid rgba(136, 136, 136, 0.8)",
    height: "8px",
    borderRadius: "5px", // Rounded corners
    backgroundColor: "white", // White background
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#EB6608", // Progress bar color
      borderRadius: "5px",
    },
    marginBottom: "1rem",
  },
  glowEffect: {
    boxShadow: "0 0 5px 0.5px #8DC63F",
  },
  // A wrong answer is felt, not just read
  shake: {
    animation: "$shake 420ms cubic-bezier(0.36, 0.07, 0.19, 0.97)",
  },
  "@keyframes shake": {
    "10%, 90%": { transform: "translateX(-2px)" },
    "20%, 80%": { transform: "translateX(4px)" },
    "30%, 50%, 70%": { transform: "translateX(-7px)" },
    "40%, 60%": { transform: "translateX(7px)" },
  },
  // A right answer settles before the next question
  correct: {
    animation: "$pop 380ms cubic-bezier(0.22, 1, 0.36, 1)",
  },
  "@keyframes pop": {
    "0%": { transform: "scale(1)" },
    "45%": { transform: "scale(1.04)" },
    "100%": { transform: "scale(1)" },
  },
  // Each question arrives instead of appearing
  question: {
    animation: "$fadeIn 350ms ease both",
  },
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  appBar: {
    position: "relative",
    backgroundColor: "transparent",
    boxShadow: "none",
  },
}));

const Quiz: React.FC = () => {
  // iPhone SE and the like: about 560 px of usable height for a question, four answers and the clock
  const shortScreen = useMediaQuery("(max-height:700px)");
  const totalScore = 0;
  const [t, i18n] = useTranslation("global");
  const dispatch = useDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const score = useSelector((state: RootState) => state.score.quizScore);
  const points = useSelector((state: RootState) => state.score.points);
  const duration = useSelector((state: RootState) => state.score.duration);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const navigate = useNavigate();
  const classes = useStyles();
  const [startTime, setStartTime] = useState(Date.now());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(60);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [ans, setAns] = useState(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [timerExpired, setTimerExpired] = useState(false); // Track if timer has expired
  // When the time runs out, the right answer is shown in green for a moment before the next question
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [isQuizfinished, setIsQuizfinished] = useState<boolean>(false);
  const videoUrl: any = sessionStorage.getItem("videoUrl");
  const userId: any = sessionStorage.getItem("InvitedUserId");
  const tenantId: any = sessionStorage.getItem("tenantId");
  // After a reload the store is empty - the breef is fetched again from its id in sessionStorage
  const storeBreef = useSelector((state: RootState) => state.score.breefDetails);
  const breefDetails = useBreefDetails() || storeBreef;
  const quizChances = useSelector((state: RootState) => state.score.quizChances);
  const [isTracked, setIsTracked] = useState(false);
  const [totalTimeTaken, setTotalTimeTaken] = useState<number>();
  const questions: Question[] = breefDetails.questions;
  const lastClickTimeRef = useRef<number>(startTime);

  useEffect(() => {
    if (!isTracked) {
      analytics.track("Quiz Page Viewed", {
        breef_id: breefDetails.id,
        tenant_id: tenantId,
        user_id: userId,
        number_of_qustions: questions.length,
        start_date: "",
        expiry_date: breefDetails.expiry,
        video_url: videoUrl,
        current_page: "quiz-info",
        game_name: breefDetails.gameId.gameName,
      });
      setIsTracked(true);
    }
  }, [isTracked]);

  const handleClose = (
    event: Event | React.SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (timeRemaining > 0) {
        setTimeRemaining((time) => time - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeRemaining]);

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  useEffect(() => {
    // Not when a right answer is already moving on to the next question (that would skip one)
    if (timeRemaining !== 0 || advancingRef.current) return undefined;
    setTimerExpired(true); // Mark timer as expired
    // Nobody got it right in time: show which answer was the right one, then carry on
    advancingRef.current = true;
    setRevealAnswer(true);
    const revealTimer = setTimeout(() => {
      advancingRef.current = false;
      setRevealAnswer(false);
      handleNextQuestion();
    }, REVEAL_MS);
    return () => clearTimeout(revealTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining]);

  useEffect(() => {
    const handlecreateBreefReport = async () => {
      try {
        // 0: the server scores the breef from the question data sent with the report (admin → Breef-indstillinger)
        await BreefReportService.createBreefReport(0, duration, 0, quizChances, setSnackbarMessage, setSnackbarSeverity, setSnackbarOpen);
        dispatch(setQuizScore(Number(sessionStorage.getItem("quizScore")) || 0));
        sessionStorage.setItem("gameScore", "0");
      } catch (error) {
        console.error("Error updating breef report:", error);
      }

      setTimeout(async () => {
        navigate("/game");
      }, 1000);

      try {
        await BreefReportService.updateBreefStatus();
      } catch (error) {
        setSnackbarMessage(t("messages.statusFailed"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.error("Error updating breef status:", error);
      }
    };

    isQuizfinished && handlecreateBreefReport();
  }, [isQuizfinished]);

  const handleNextQuestion = async () => {
    // The question ended without a right answer (time ran out)
    if (!advancingRef.current) {
      recordQuestion({ index: currentQuestionIndex, attempts: attemptsRef.current, timeMs: Date.now() - questionStartedAtRef.current, correctFirstTry: false, correct: false });
    }
    questionStartedAtRef.current = Date.now();
    const currentTime = Date.now();
    const TimeTaken = (currentTime - lastClickTimeRef.current) / 1000;
    lastClickTimeRef.current = currentTime;
    setTotalTimeTaken(TimeTaken);
    advancingRef.current = false;
    setSelectedOptionIndex(null);
    setIsCorrect(null);
    setTimerExpired(false); // Reset timer expired state
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((index) => index + 1);
      setTimeRemaining(() => 60); // Reset timer for the next question
      setStartTime(() => Date.now());
      // Reset attempts for the next question
      attemptsRef.current = 0;
    } else {
      analytics.track("Answer selected", {
        breef_id: breefDetails.id,
        tenant_id: tenantId,
        user_id: userId,
        number_of_qustions: questions.length,
        start_date: breefDetails.period,
        expiry_date: breefDetails.expiry,
        video_url: videoUrl,
        current_page: "quiz",
        game_name: breefDetails.gameId.gameName,
        attempt_no: attemptsRef.current,
        time_taken_to_complete_quiz: totalTimeTaken,
      });
      setIsQuizfinished(true);
    }
  };
  const attemptsRef = useRef(0);
  // When the current question was shown (learning data: time per question)
  const questionStartedAtRef = useRef(Date.now());
  // Clicks during the pause after a right answer are ignored (they used to add points again and skip questions)
  const advancingRef = useRef(false);
  // Clicks over the whole quiz: a quiz where nothing was ever answered scores 0
  const totalAttemptsRef = useRef(0);
  const handleOptionClick = async (optionIndex: number) => {
    if (advancingRef.current) return;
    attemptsRef.current++;
    totalAttemptsRef.current++;

    const correct =
      questions[currentQuestionIndex]?.options[optionIndex] ===
      questions[currentQuestionIndex]?.answer.toString();
    setSelectedOptionIndex(optionIndex);
    setIsCorrect(correct);
    console.log("Attempts for current question: ", attemptsRef.current);

    if (correct) {
      analytics.track("Quiz submitted", {
        breef_id: breefDetails.id,
        tenant_id: tenantId,
        user_id: userId,
        number_of_qustions: questions.length,
        start_date: breefDetails.period,
        expiry_date: breefDetails.expiry,
        video_url: videoUrl,
        current_page: "quiz",
        game_name: breefDetails.gameId.gameName,
        question: currentQuestion,
        is_correct_answer: `${correct ? "yes" : "no"}`,
        attempt_no: attemptsRef.current,
        time_taken_to_answer: totalTimeTaken,
      });
      dispatch(setQuizChance(attemptsRef.current)); // Increment chances only when correct
      recordQuestion({
        index: currentQuestionIndex,
        attempts: attemptsRef.current,
        timeMs: Date.now() - questionStartedAtRef.current,
        correctFirstTry: attemptsRef.current === 1,
        correct: true,
      });
      setAns((prev) => prev + 1);
      advancingRef.current = true;
      setTimeout(() => {
        handleNextQuestion();
      }, 1000);
    } else {
      // Wrong: it shows for a moment and shakes, then the board goes back so the next try starts clean
      window.setTimeout(() => {
        setSelectedOptionIndex(null);
        setIsCorrect(null);
      }, 700);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  /** The answer is stored as the text of the right option */
  const isRightOption = (option: string) => option === String(currentQuestion?.answer ?? "");
  const progress = ((60 - timeRemaining) / 60) * 100;

  return (
    <MobileLayout>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar>{/* <img src={breeferLogo} alt="logo" height="80 rem" /> */}</Toolbar>
      </AppBar>
      {questions.length > 0 && (
        <>
          {" "}
          <div style={{ height: "calc(100dvh - 5rem)", width: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
            {/* <Box mt={3}>
                    <Typography variant="h6" align="center" style={{ marginTop: '10%' }}>
                        <span style={{ color: '#E2DFD2' }}>lorem10dijwemiuwejiwejwidmodmweomweodiwecmdijcmedceifmeifecmeciemcie
                          
                        </span>
                    </Typography>
                </Box> */}
            <div
              key={currentQuestionIndex}
              className={classes.question}
              style={{
                margin: shortScreen ? "4% 8% 2%" : "8% 10% 4%",
                display: "flex",
                maxWidth: "100%",
                justifyContent: "center",
                alignContent: "center",
                wordWrap: "normal",
                wordBreak: "break-word",
              }}
            >
              <span
                style={{
                  letterSpacing: "0.05em",
                  fontSize: shortScreen ? "1.6rem" : "2rem",
                  fontWeight: "350",
                  color: "#000000",
                  textAlign: "center",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  wordWrap: "normal",
                  wordBreak: "break-word",
                }}
              >
                {currentQuestion?.question}
              </span>
            </div>
            {/* How far you are: without it, nobody knows whether the 60 seconds are the last ones */}
            <Typography align="center" style={{ fontFamily: "Roboto", fontSize: "0.95rem", color: "#666", marginTop: "-2%" }}>
              {t("quiz.progress", { current: currentQuestionIndex + 1, total: questions.length })}
            </Typography>

            <Box style={{ margin: shortScreen ? "2% 12% 0%" : "4% 16% 0%", flex: 1, minHeight: 0, overflowY: "auto" }}>
              {currentQuestion?.options.map((option, index) => (
                <Button
                  key={index}
                  fullWidth
                  variant={selectedOptionIndex === index ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => {
                    handleOptionClick(index);
                  }}

                  style={{
                    textTransform: "none",
                    lineHeight: "20px",
                    marginBottom: shortScreen ? "0.6rem" : "1rem",
                    borderRadius: "14px",
                    fontSize: "1rem",
                    padding: shortScreen ? "0.7rem" : "1rem",
                    fontWeight: "900",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                    borderColor: revealAnswer && isRightOption(option) ? "#8DC63F" : "#000000",
                    backgroundColor:
                      revealAnswer && isRightOption(option)
                        ? "#8DC63F"
                        : selectedOptionIndex === index
                        ? isCorrect
                          ? "#8DC63F"
                          : "#FF0000"
                        : "#FFFFFF", // the other options are left alone
                    color:
                      (revealAnswer && isRightOption(option)) ||
                      (selectedOptionIndex !== null && index === selectedOptionIndex && isCorrect)
                        ? "white"
                        : "#000000",
                    boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.6)",
                  }}
                  disabled={timerExpired && selectedOptionIndex === null}
                  className={[
                    revealAnswer && isRightOption(option) ? classes.glowEffect : "",
                    selectedOptionIndex === index && isCorrect === false ? classes.shake : "",
                    selectedOptionIndex === index && isCorrect === true ? classes.correct : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {option}
                </Button>
              ))}
            </Box>
            <Box style={{ margin: shortScreen ? "0 17% 3%" : "0 17% 6%", flexShrink: 0 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.linearProgress}
                style={{ boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.6)" }}
              />
              <Typography
                variant="body2"
                align="center"
                style={{ fontSize: "2rem", fontWeight: "300", color: timeRemaining <= 10 ? "#D32F2F" : undefined, transition: "color 300ms ease" }}
              >{`${String(Math.floor(timeRemaining / 60)).padStart(2, "0")}:${String(
                timeRemaining % 60
              ).padStart(2, "0")}`}</Typography>
            </Box>
          </div>
          {/* <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={handleClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar> */}
        </>
      )}
    </MobileLayout>
  );
};

export default Quiz;
